import type {
  AdjustmentWithCreator,
  AdjustmentListResponse,
  CreateAdjustmentDto,
  UpdateAdjustmentDto,
  AdjustmentListQuery,
  AdjustmentStatus,
  AdjustmentResult,
  UserExpense,
  TransferInfo,
} from "@repo/share-log-interfaces";
import { z } from "zod";
import type { HttpClient } from "../http";
import type { DataAndMessageResponse, MessageResponse } from "../types";

// Schemas
const TransferInfoSchema: z.ZodType<TransferInfo> = z.object({
  fromUserId: z.number(),
  fromUserNickname: z.string(),
  toUserId: z.number(),
  toUserNickname: z.string(),
  amount: z.number(),
});

const UserExpenseSchema: z.ZodType<UserExpense> = z.object({
  userId: z.number(),
  nickname: z.string(),
  totalExpense: z.number(),
  shouldPay: z.number(),
  difference: z.number(),
});

const AdjustmentResultSchema: z.ZodType<AdjustmentResult> = z.object({
  totalExpense: z.number(),
  perPersonAmount: z.number(),
  userExpenses: z.array(UserExpenseSchema),
  transfers: z.array(TransferInfoSchema),
});

const AdjustmentWithCreatorSchema: z.ZodType<AdjustmentWithCreator> = z.object({
  id: z.number(),
  workspaceId: z.number(),
  name: z.string(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  categoryIds: z.array(z.number()),
  methodIds: z.array(z.number()),
  participantIds: z.array(z.number()),
  status: z.enum(["created", "completed"]) as z.ZodType<AdjustmentStatus>,
  result: AdjustmentResultSchema.nullish(),
  creatorId: z.number(),
  completedAt: z.coerce.date().nullish(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  creator: z.object({
    id: z.number(),
    nickname: z.string().nullish(),
    avatarUrl: z.string().nullish(),
  }),
});

const AdjustmentListResponseSchema: z.ZodType<AdjustmentListResponse> =
  z.object({
    adjustments: z.array(AdjustmentWithCreatorSchema),
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    hasMore: z.boolean(),
  });

// Response Schemas
const CreateAdjustmentResponseSchema: z.ZodType<
  DataAndMessageResponse<AdjustmentWithCreator>
> = z.object({
  data: AdjustmentWithCreatorSchema,
  message: z.string(),
});

const GetAdjustmentsResponseSchema: z.ZodType<
  DataAndMessageResponse<AdjustmentListResponse>
> = z.object({
  data: AdjustmentListResponseSchema,
  message: z.string(),
});

const GetAdjustmentResponseSchema: z.ZodType<
  DataAndMessageResponse<AdjustmentWithCreator>
> = z.object({
  data: AdjustmentWithCreatorSchema,
  message: z.string(),
});

const UpdateAdjustmentResponseSchema: z.ZodType<
  DataAndMessageResponse<AdjustmentWithCreator>
> = z.object({
  data: AdjustmentWithCreatorSchema,
  message: z.string(),
});

const DeleteAdjustmentResponseSchema: z.ZodType<MessageResponse> = z.object({
  message: z.string(),
});

const CompleteAdjustmentResponseSchema: z.ZodType<
  DataAndMessageResponse<AdjustmentWithCreator>
> = z.object({
  data: AdjustmentWithCreatorSchema,
  message: z.string(),
});

export function createAdjustmentModule(httpClient: HttpClient) {
  return {
    create: async (workspaceId: number, data: CreateAdjustmentDto) => {
      const response = await httpClient.post<
        typeof CreateAdjustmentResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/adjustments`, {
        data,
        schema: CreateAdjustmentResponseSchema,
      });
      return response.data;
    },

    list: async (workspaceId: number, query?: AdjustmentListQuery) => {
      const params = new URLSearchParams();
      if (query?.status) params.set("status", query.status);
      if (query?.page) params.set("page", String(query.page));
      if (query?.limit) params.set("limit", String(query.limit));

      const queryString = params.toString();
      const url = `/api/v1/workspaces/${workspaceId}/adjustments${queryString ? `?${queryString}` : ""}`;

      const response = await httpClient.get<
        typeof GetAdjustmentsResponseSchema
      >(url, {
        schema: GetAdjustmentsResponseSchema,
      });
      return response.data;
    },

    get: async (workspaceId: number, adjustmentId: number) => {
      const response = await httpClient.get<typeof GetAdjustmentResponseSchema>(
        `/api/v1/workspaces/${workspaceId}/adjustments/${adjustmentId}`,
        {
          schema: GetAdjustmentResponseSchema,
        },
      );
      return response.data;
    },

    update: async (
      workspaceId: number,
      adjustmentId: number,
      data: UpdateAdjustmentDto,
    ) => {
      const response = await httpClient.patch<
        typeof UpdateAdjustmentResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/adjustments/${adjustmentId}`, {
        data,
        schema: UpdateAdjustmentResponseSchema,
      });
      return response.data;
    },

    remove: async (workspaceId: number, adjustmentId: number) => {
      const response = await httpClient.delete<
        typeof DeleteAdjustmentResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/adjustments/${adjustmentId}`, {
        schema: DeleteAdjustmentResponseSchema,
      });
      return response;
    },

    complete: async (workspaceId: number, adjustmentId: number) => {
      const response = await httpClient.post<
        typeof CompleteAdjustmentResponseSchema
      >(
        `/api/v1/workspaces/${workspaceId}/adjustments/${adjustmentId}/complete`,
        {
          schema: CompleteAdjustmentResponseSchema,
        },
      );
      return response.data;
    },
  };
}
